import { useSearch } from '@hero/hooks-shared';
import { convertToLatLng } from '@hero/core';
import { MapLocationData } from '@hero/web-components';
import { useAutocompleteApi } from '@hero/api-hooks';

export interface UseLocationAutocompleteOptions {
  /** Minimum search length before triggering query */
  minSearchLength?: number;
  /** Debounce delay in milliseconds */
  debounceMs?: number;
  /** Whether the query is enabled */
  enabled?: boolean;
  /** Organization ID for filtering saved locations */
  orgId?: string;
  /** Client ID for filtering saved locations (third-party counterparty). */
  clientId?: string;
}

/**
 * Hook for location autocomplete search.
 * Supports both Google Places and saved locations. Uses useAutocompleteApi() (query) internally.
 */
export function useLocationAutocomplete(search: string, options: UseLocationAutocompleteOptions) {
  const { minSearchLength, debounceMs = 300, enabled = true, orgId, clientId } = options;

  const { searchReady } = useSearch({
    searchValue: search,
    delay: debounceMs,
    minSearchLength,
    onSearchReadyChange: () => {},
  });

  const query = useAutocompleteApi({ search: searchReady }, { enabled });

  const autocompleteData = query.data;

  // Convert saved locations to MapLocationData format
  const savedLocationsAsMapData: MapLocationData[] =
    autocompleteData?.savedLocations?.map(savedLocation => ({
      cords: convertToLatLng(savedLocation.address.location), // Convert MongoDB format to Google Maps format
      city: savedLocation.address.city?.name,
      fullAddress: savedLocation.address.fullAddress,
      address: savedLocation.address,
      savedLocation,
      isAccurateLocation: true, // Saved locations are accurate by default
    })) || [];

  // Convert Google Places to MapLocationData format
  const googlePlacesAsMapData: MapLocationData[] =
    autocompleteData?.googlePlaces?.map(place => ({
      cords: place.location,
      city: undefined, // Will be fetched when selected
      fullAddress: place.fullAddress,
      isAccurateLocation: false, // Google Places are inaccurate by default
    })) || [];

  return {
    ...query,
    googlePlaces: autocompleteData?.googlePlaces || [],
    savedLocations: autocompleteData?.savedLocations || [],
    googlePlacesAsMapData,
    savedLocationsAsMapData,
    isLoading: query.isLoading && searchReady.length > 0,
  };
}
